import React, { useState, useMemo, useEffect, useRef } from "react";
import ExcelJS from "exceljs";
import {
  FaSort,
  FaSortUp,
  FaSortDown,
  FaDownload,
  FaFileExcel,
  FaFilePdf,
  FaEraser,
  FaChevronLeft,
  FaChevronRight,
  FaFilter
} from "react-icons/fa";
import Modal from "./Modal";
import AutocompleteSocio from "./AutocompleteSocio";
import { useAuth } from "../providers/AuthContext";
import { getFamilyGroups, getFamilyGroupById } from "../services/apiFamilyGroups";
import { getUserByEmail, getFamilyMembers, getAllFusedUsers } from "../services/apiUsers";
import { getAccountStatement, generateAccountStatementPDF } from "../services/apiTransactions";

const useTheme = () => ({
  colors: {
    backgroundSurface: "#ffffff",
    borderDefault: "#e5e7eb",
    textPrimary: "#111827",
    textSecondary: "#6b7280",
    backgroundApp: "#f9fafb"
  }
});

const PAGE_OPTIONS = [10, 25, 50, 100, 500];
const STORAGE_KEY = "FilterTableWidget_pageSize";

const FilterTableWidget = ({
  title = "Tabla Filtrable",
  data = [],
}) => {
  const { colors } = useTheme();

  // --- ESTADOS ---
  const [pageSize, setPageSize] = useState(100);
  const [columnFilters, setColumnFilters] = useState({});
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [page, setPage] = useState(1);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfReportType, setPdfReportType] = useState('Socio'); // 'Grupo Familiar' o 'Socio'
  const [pdfFamilyGroup, setPdfFamilyGroup] = useState('');
  const [pdfCedula, setPdfCedula] = useState('');

  const getDefaultStartDate = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    return `${yyyy}-${mm}-01`;
  };

  const getDefaultEndDate = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const [pdfStartDate, setPdfStartDate] = useState(getDefaultStartDate());
  const [pdfEndDate, setPdfEndDate] = useState(getDefaultEndDate());
  const [familyGroupsList, setFamilyGroupsList] = useState([]);
  const [isLeader, setIsLeader] = useState(false);
  const [userFamilyGroupId, setUserFamilyGroupId] = useState(null);
  const [userFamilyGroupName, setUserFamilyGroupName] = useState('');
  const [userCedula, setUserCedula] = useState('');
  const [familyMembersList, setFamilyMembersList] = useState([]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { auth, hasPermission, userEmail } = useAuth() || {};
  const hasReadTransactions = hasPermission ? hasPermission('accionistas_transactions', 'transactions', 'readAll') : false;
  const canSeeSelector = hasReadTransactions || isLeader;

  const showError = (msg) => {
    setErrorMessage(msg);
    setErrorModalOpen(true);
  };

  // --- EFECTOS ---
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const v = Number(saved);
      if (v !== 100) {
        localStorage.setItem(STORAGE_KEY, "100");
        setPageSize(100);
      } else {
        setPageSize(v);
      }
    } else {
      localStorage.setItem(STORAGE_KEY, "100");
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(pageSize));
  }, [pageSize]);

  useEffect(() => {
    if (isPdfModalOpen && auth?.token && userEmail) {
      getUserByEmail(userEmail, auth.token, true).then(user => {
        if (user) {
          setIsLeader(user.isFamilyLeader);
          setUserFamilyGroupId(user.familyGroupId);
          setUserCedula(user.cedula || '');
          setPdfCedula(user.familyGroupId ? (user.cedula || '') : '');
          if (hasReadTransactions) {
             getAllFusedUsers(auth.token).then(members => {
                setFamilyMembersList(members);
             }).catch(console.error);
          } else if (user.familyGroupId) {
             getFamilyMembers(user.familyGroupId, auth.token).then(members => {
                setFamilyMembersList(members);
             }).catch(console.error);
          } else {
             const defaultName = user.username || 'Usuario Actual';
             setFamilyMembersList([{ _id: user._id, username: defaultName, cedula: user.cedula }]);
          }
        }
      }).catch(console.error);
    }
  }, [isPdfModalOpen, auth, userEmail, hasReadTransactions]);

  useEffect(() => {
    if (isPdfModalOpen && pdfReportType === 'Grupo Familiar' && auth?.token) {
      if (hasReadTransactions) {
        getFamilyGroups('', auth.token, true).then(res => {
          setFamilyGroupsList(res || []);
        }).catch(console.error);
      } else if (isLeader && userFamilyGroupId) {
        getFamilyGroupById(userFamilyGroupId, auth.token, true).then(myGroup => {
          if (myGroup) {
             setUserFamilyGroupName(myGroup.nombre);
             setPdfFamilyGroup(myGroup._id);
          }
        }).catch(console.error);
      }
    }
  }, [isPdfModalOpen, pdfReportType, hasReadTransactions, isLeader, userFamilyGroupId, auth]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  // --- LÓGICA DE FILTRADO ---
  const columns = useMemo(() => (data[0] ? Object.keys(data[0]) : []), [data]);

  const matchFilter = (value, filter) => {
    if (filter.trim() === "") return true;
    const f = filter.trim();

    // Si el usuario apenas escribió el operador, no filtrar nada todavía
    if (/^[<>=]+$/.test(f)) return true;

    const lowerVal = String(value ?? "").toLowerCase();
    const rawFilterVal = f.replace(/[<>=]/g, "").trim();

    const isNumericFilter = rawFilterVal !== "" && !isNaN(Number(rawFilterVal));
    const isNumericValue = value !== null && value !== "" && !isNaN(Number(value));

    const parseDateString = (str) => {
      if (!str) return NaN;
      let timestamp = Date.parse(str);
      if (!isNaN(timestamp) && isNaN(Number(str))) return timestamp;

      const regex = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/;
      const match = String(str).match(regex);
      if (match) {
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1;
        const year = parseInt(match[3], 10);
        return new Date(year, month, day).getTime();
      }
      return NaN;
    };

    const parsedFilterDate = parseDateString(rawFilterVal);
    const parsedValueDate = parseDateString(value);
    const isDateFilter = !isNumericFilter && !isNaN(parsedFilterDate) && !isNaN(parsedValueDate);

    if (f.startsWith(">=")) {
      if (isNumericFilter && isNumericValue) return Number(value) >= Number(rawFilterVal);
      if (isDateFilter) return parsedValueDate >= parsedFilterDate;
    }
    if (f.startsWith("<=")) {
      if (isNumericFilter && isNumericValue) return Number(value) <= Number(rawFilterVal);
      if (isDateFilter) return parsedValueDate <= parsedFilterDate;
    }
    if (f.startsWith(">")) {
      if (isNumericFilter && isNumericValue) return Number(value) > Number(rawFilterVal);
      if (isDateFilter) return parsedValueDate > parsedFilterDate;
    }
    if (f.startsWith("<")) {
      if (isNumericFilter && isNumericValue) return Number(value) < Number(rawFilterVal);
      if (isDateFilter) return parsedValueDate < parsedFilterDate;
    }
    if (f.startsWith("=")) {
      return lowerVal === rawFilterVal.toLowerCase();
    }

    return lowerVal.includes(f.toLowerCase());
  };

  const filtered = useMemo(() => {
    return data.filter((row) =>
      Object.entries(columnFilters).every(([col, val]) =>
        val.trim() ? matchFilter(row[col], val) : true
      )
    );
  }, [data, columnFilters]);

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sort]);

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginated = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize]
  );

  const toggleSort = (key) =>
    setSort((p) =>
      p.key === key
        ? { key, dir: p.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );

  const hasActiveFilters = Object.values(columnFilters).some(v => v.trim() !== '');

  const handleExportExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Reporte");

      worksheet.addRow(columns);

      sorted.forEach((row) => {
        const rowData = columns.map((col) => {
          let val = row[col];
          if (val === null || val === undefined) return "";
          if (typeof val === 'object') return JSON.stringify(val);
          return val;
        });
        worksheet.addRow(rowData);
      });

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const hh = String(now.getHours()).padStart(2, '0');
      const min = String(now.getMinutes()).padStart(2, '0');
      const ss = String(now.getSeconds()).padStart(2, '0');
      const dateStr = `${dd}-${mm}-${yyyy}_${hh}-${min}-${ss}`;

      const safeTitle = title ? title.replace(/[^a-z0-9]/gi, '_').toLowerCase() : "transacciones";
      a.download = `${safeTitle}_${dateStr}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al exportar a Excel:", error);
    }
  };

  const handleGeneratePDF = async () => {
    if (!auth?.token) {
      showError("No hay token de sesión válido.");
      return;
    }
    
    let filters = {};

    try {
      setIsGeneratingPdf(true);
      if (pdfStartDate) filters.fechaInicio = pdfStartDate;
      if (pdfEndDate) filters.fechaFin = pdfEndDate;
      if (!canSeeSelector) {
        filters.mode = 'socio';
        filters.targetId = pdfCedula || userCedula;
      } else {
        if (pdfReportType === 'Grupo Familiar') {
          filters.mode = 'familyGroup';
          if (pdfFamilyGroup) filters.targetId = pdfFamilyGroup;
          else if (userFamilyGroupId) filters.targetId = userFamilyGroupId;
        } else {
          filters.mode = 'socio';
          if (pdfCedula) filters.targetId = pdfCedula;
        }
      }

      if (!filters.targetId) {
        if (!canSeeSelector) {
           showError("No se encontró su número de cédula en su perfil de usuario.");
        } else {
           showError(filters.mode === 'socio' ? "El campo Cédula del Socio está vacío." : "El campo Grupo Familiar no se ha seleccionado.");
        }
        setIsGeneratingPdf(false);
        return;
      }

      if (!filters.fechaInicio) {
        showError("El campo Fecha Inicial está vacío.");
        setIsGeneratingPdf(false);
        return;
      }

      if (!filters.fechaFin) {
        showError("El campo Fecha Final está vacío.");
        setIsGeneratingPdf(false);
        return;
      }

      if (new Date(filters.fechaInicio) > new Date(filters.fechaFin)) {
        showError("La fecha inicial no puede ser mayor a la fecha final.");
        setIsGeneratingPdf(false);
        return;
      }

      const result = await getAccountStatement(auth.token, filters);

      await generateAccountStatementPDF(auth.token, result);
      setIsPdfModalOpen(false);
    } catch (error) {
      console.error("Error al generar PDF:", error);
      const errorMsg = error.message || "";
      if (filters.mode === 'socio' && (errorMsg.toLowerCase().includes("no encontrad") || errorMsg.toLowerCase().includes("not found") || errorMsg.toLowerCase().includes("404"))) {
         showError("No se encontró usuario con la información proporcionada.");
      } else {
         showError(errorMsg);
      }
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // --- RENDER ---
  return (
    <div
      className="flex flex-col h-full rounded-lg shadow-sm border transition-all duration-200"
      style={{
        backgroundColor: colors.backgroundSurface,
        borderColor: colors.borderDefault,
        color: colors.textPrimary,
        fontFamily: 'inherit'
      }}
    >
      {/* 🔹 HEADER SUPERIOR */}
      <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: colors.borderDefault }}>
        <div className="flex flex-col">
          <h3 className="font-bold text-lg leading-tight flex items-center gap-2" style={{ color: colors.textPrimary }}>
            {title}
            {hasActiveFilters && <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">Filtrado</span>}
          </h3>
          <span className="text-xs mt-1" style={{ color: colors.textSecondary }}>
            Mostrando {filtered.length} registros
          </span>
        </div>

        {/* Botón Exportación */}
        {sorted.length > 0 && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 rounded-md hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              style={{ color: colors.textSecondary }}
              title="Descargar"
            >
              <FaDownload size={16} />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-56 rounded-md shadow-lg border z-50 overflow-hidden py-1"
                style={{ backgroundColor: colors.backgroundSurface, borderColor: colors.borderDefault }}
              >
                <button onClick={() => { handleExportExcel(); setMenuOpen(false); }} className="flex items-center w-full px-4 py-2 text-sm hover:bg-black/5 dark:hover:bg-white/5" style={{ color: colors.textPrimary }}>
                  <FaFileExcel className="mr-3 text-green-600 flex-shrink-0" /> Reporte Tabla (Excel)
                </button>
                <button onClick={() => { setIsPdfModalOpen(true); setMenuOpen(false); }} className="flex items-center w-full px-4 py-2 text-sm hover:bg-black/5 dark:hover:bg-white/5" style={{ color: colors.textPrimary }}>
                  <FaFilePdf className="mr-3 text-red-500 flex-shrink-0" /> Estado de Cuenta (PDF)
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 🔹 BARRA DE HERRAMIENTAS */}
      <div className="flex flex-wrap items-center justify-between p-3 gap-3 bg-opacity-50 border-b"
        style={{ backgroundColor: colors.backgroundApp, borderColor: colors.borderDefault }}
      >
        <button
          onClick={() => {
            setColumnFilters({});
            setSort({ key: null, dir: "asc" });
            setPage(1);
          }}
          disabled={!hasActiveFilters}
          className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-all border ${
            !hasActiveFilters ? 'opacity-50 cursor-not-allowed' : 'hover:bg-black/5 dark:hover:bg-white/5'
          }`}
          style={{
            color: colors.textPrimary,
            backgroundColor: colors.backgroundSurface,
            borderColor: colors.borderDefault
          }}
        >
          <FaEraser /> Limpiar Filtros
        </button>

        <div className="flex items-center gap-2 text-xs">
          <span style={{ color: colors.textSecondary }}>Filas:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="px-2 py-1 rounded-md border text-xs cursor-pointer outline-none focus:border-primary"
            style={{
              backgroundColor: colors.backgroundSurface,
              borderColor: colors.borderDefault,
              color: colors.textPrimary
            }}
          >
            {PAGE_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
      </div>

      {/* 🔹 TABLA PRINCIPAL */}
      <div className="flex-1 overflow-auto relative">
        <table className="w-full text-left border-collapse">
          <thead
            className="sticky top-0 z-10 text-xs font-semibold shadow-sm"
            style={{
              backgroundColor: colors.backgroundApp,
              color: colors.textSecondary,
              borderBottom: `1px solid ${colors.borderDefault}`
            }}
          >
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  className="px-4 py-2 cursor-pointer select-none hover:text-primary transition-colors group"
                  onClick={() => toggleSort(col)}
                >
                  <div className="flex items-center gap-1 uppercase tracking-wider">
                    {col}
                    <span className="opacity-0 group-hover:opacity-50 transition-opacity" style={{ opacity: sort.key === col ? 1 : undefined }}>
                      {sort.key === col ? (sort.dir === "asc" ? <FaSortUp /> : <FaSortDown />) : <FaSort />}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
            <tr>
              {columns.map((col) => (
                <th key={`filter-${col}`} className="px-4 pb-2 pt-0">
                  <div className="relative">
                    <input
                      value={columnFilters[col] || ""}
                      onChange={(e) => {
                        setColumnFilters((prev) => ({ ...prev, [col]: e.target.value }));
                        setPage(1);
                      }}
                      placeholder="Filtrar (>, <, =)"
                      className="w-full px-2 py-1 text-xs rounded border outline-none focus:ring-1 focus:ring-opacity-50 transition-all font-normal"
                      style={{
                        backgroundColor: colors.backgroundSurface,
                        borderColor: colors.borderDefault,
                        color: colors.textPrimary,
                        '--tw-ring-color': colors.primary
                      }}
                    />
                    {columnFilters[col] && (
                      <div className="absolute right-2 top-1.5 text-[8px] opacity-50 pointer-events-none">
                         <FaFilter />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="text-sm">
            {paginated.length > 0 ? (
              paginated.map((r, i) => (
                <tr
                  key={i}
                  className="hover:bg-black/5 dark:hover:bg-white/5 transition-colors group"
                  style={{ borderBottom: `1px solid ${colors.borderDefault}` }}
                >
                  {columns.map((c) => (
                    <td
                      key={c}
                      className="px-4 py-3 whitespace-nowrap truncate max-w-[180px]"
                      title={String(r[c])}
                    >
                      {String(r[c])}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-12 text-center italic"
                  style={{ color: colors.textSecondary }}
                >
                  No se encontraron resultados con los filtros aplicados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 🔹 FOOTER */}
      {totalPages > 1 && (
        <div className="p-3 border-t flex justify-end items-center gap-2" style={{ borderColor: colors.borderDefault }}>
          <button
            onClick={() => page > 1 && setPage(page - 1)}
            disabled={page === 1}
            className={`p-1.5 rounded-md border transition-all ${page === 1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
            style={{ borderColor: colors.borderDefault, color: colors.textSecondary }}
          >
            <FaChevronLeft size={12} />
          </button>

          <div className="flex items-center gap-1 text-xs font-medium">
            <span style={{ color: colors.textPrimary }}>Página</span>
            <input
              type="number"
              value={page}
              min={1}
              max={totalPages}
              onChange={(e) => {
                const val = Number(e.target.value);
                if (val >= 1 && val <= totalPages) setPage(val);
              }}
              className="w-10 text-center p-1 rounded border outline-none focus:border-primary"
              style={{
                backgroundColor: colors.backgroundApp,
                borderColor: colors.borderDefault,
                color: colors.textPrimary
              }}
            />
            <span style={{ color: colors.textSecondary }}>de {totalPages}</span>
          </div>

          <button
            onClick={() => page < totalPages && setPage(page + 1)}
            disabled={page === totalPages}
            className={`p-1.5 rounded-md border transition-all ${page === totalPages ? 'opacity-40 cursor-not-allowed' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
            style={{ borderColor: colors.borderDefault, color: colors.textSecondary }}
          >
            <FaChevronRight size={12} />
          </button>
        </div>
      )}

      <Modal
        isOpen={isPdfModalOpen}
        onClose={() => setIsPdfModalOpen(false)}
        title="Generar Estado de Cuenta"
      >
        <div className="p-6 text-left">
          {canSeeSelector && (
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Reporte</label>
              <select
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#10174c]"
                value={pdfReportType}
                onChange={(e) => setPdfReportType(e.target.value)}
              >
                <option value="Socio">Reporte por Socio</option>
                <option value="Grupo Familiar">Reporte por Grupo Familiar</option>
              </select>
            </div>
          )}

          {canSeeSelector && pdfReportType === 'Grupo Familiar' && (
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-1">Grupo Familiar</label>
              {hasReadTransactions ? (
                <select
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#10174c]"
                  value={pdfFamilyGroup}
                  onChange={(e) => setPdfFamilyGroup(e.target.value)}
                >
                  <option value="">Seleccione un grupo...</option>
                  {Array.isArray(familyGroupsList) ? familyGroupsList.map((g) => (
                    <option key={g._id} value={g._id}>{g.nombre}</option>
                  )) : null}
                </select>
              ) : (
                <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100 text-gray-700">
                  {userFamilyGroupName || "Cargando..."}
                </div>
              )}
            </div>
          )}

          {(!canSeeSelector || pdfReportType === 'Socio') && (
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-1">Socio</label>
              {hasReadTransactions || isLeader ? (
                <AutocompleteSocio
                  options={familyMembersList}
                  value={pdfCedula}
                  onChange={(newCedula) => setPdfCedula(newCedula)}
                  placeholder="Escriba para buscar un socio..."
                />
              ) : (
                <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100 text-gray-700">
                  {familyMembersList.find(m => m.cedula === userCedula)?.username || "Cargando..."}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-6 mb-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicial</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#10174c]"
                value={pdfStartDate}
                onChange={(e) => setPdfStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Final</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#10174c]"
                value={pdfEndDate}
                onChange={(e) => setPdfEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-8">
            <button
              onClick={handleGeneratePDF}
              disabled={isGeneratingPdf}
              className={`px-4 py-2 bg-[#10174c] text-white rounded transition font-bold ${isGeneratingPdf ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-900'}`}
            >
              {isGeneratingPdf ? "Generando..." : "Generar PDF"}
            </button>
            <button
              onClick={() => setIsPdfModalOpen(false)}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition font-bold"
            >
              Cancelar
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        title="Error"
        colorTitle="bg-red-500"
        customType={1}
        size="max-w-sm w-full"
      >
        <div className="p-6 text-center">
          <p className="text-lg text-gray-800 mb-6">{errorMessage}</p>
          <div className="flex justify-center">
            <button
              onClick={() => setErrorModalOpen(false)}
              className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition font-bold"
            >
              Aceptar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FilterTableWidget;
